import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { getApiBase, getAuthHeaders } from '../../utils/api';
import {
  Plane,
  Hotel,
  DollarSign,
  TrendingUp,
  Building2,
  Search,
  ChevronRight,
  ChevronLeft,
  Loader2,
  AlertCircle,
  RefreshCw,
  Wallet,
  AlertTriangle,
} from 'lucide-react';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '../ui/table';

const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

const AdminTravelTransactions = ({ currentUser }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [period, setPeriod] = useState('month');

  // Overview data
  const [balanceData, setBalanceData] = useState(null);
  const [summaryData, setSummaryData] = useState(null);

  // Transactions data
  const [transactions, setTransactions] = useState([]);
  const [txnTotalCount, setTxnTotalCount] = useState(0);
  const [txnPage, setTxnPage] = useState(1);
  const txnPageSize = 50;
  const [txnFilters, setTxnFilters] = useState({
    organization_id: '',
    booking_type: '',
    payment_method: '',
    search: '',
  });
  const debouncedSearch = useDebounce(txnFilters.search, 300);

  // Profitability data
  const [orgProfitability, setOrgProfitability] = useState([]);
  const [platformTotals, setPlatformTotals] = useState(null);

  // Organizations list for filter dropdown
  const [organizations, setOrganizations] = useState([]);

  // Shared org filter for overview tab
  const [overviewOrgId, setOverviewOrgId] = useState('');

  const apiBase = getApiBase();

  // Theme-aware style helpers
  const cardBg = 'bg-gradient-to-br from-ui-hover/80 to-ui-surface/80 border-ui-line shadow-ui-card';
  const textPrimary = 'text-ui-ink';
  const textSecondary = 'text-ui-muted';
  const textTertiary = 'text-ui-ink-2';
  const inputBg = 'bg-ui-sunk border-ui-control text-ui-ink placeholder:text-ui-faint';
  const tableHeaderBg = 'bg-ui-sunk';
  const tableBorderColor = 'border-ui-line';
  const hoverBg = 'hover:bg-ui-hover';
  const greenText = 'text-ui-green-fg';
  const greenBoldText = 'text-ui-green-fg';

  // Load organizations for filter dropdown
  const loadOrganizations = useCallback(async () => {
    try {
      const res = await axios.get(`${apiBase}/admin/billing/organizations`, {
        params: { limit: 100, offset: 0 },
        headers: getAuthHeaders(),
      });
      if (res.data?.organizations) {
        // API returns { organization: { id, name }, billing_profile, ... } per item
        const orgs = res.data.organizations.map((item) => ({
          id: item.organization?.id || item.id || item.organization_id,
          name: item.organization?.name || item.name || item.organization_name || item.organization?.id || 'Unknown',
        }));
        setOrganizations(orgs);
      }
    } catch (err) {
      console.error('Failed to load organizations:', err);
    }
  }, [apiBase]);

  // Load Duffel balance
  const loadBalance = useCallback(async () => {
    try {
      const res = await axios.get(`${apiBase}/admin/travel/balance`, {
        headers: getAuthHeaders(),
      });
      if (res.data?.success) {
        setBalanceData(res.data);
      }
    } catch (err) {
      console.error('Failed to load balance:', err);
    }
  }, [apiBase]);

  // Load summary metrics
  const loadSummary = useCallback(async () => {
    try {
      const params = { period };
      if (overviewOrgId) params.organization_id = overviewOrgId;
      const res = await axios.get(`${apiBase}/admin/travel/summary`, {
        params,
        headers: getAuthHeaders(),
      });
      if (res.data?.success) {
        setSummaryData(res.data);
      }
    } catch (err) {
      console.error('Failed to load summary:', err);
    }
  }, [apiBase, period, overviewOrgId]);

  // Load transactions
  const loadTransactions = useCallback(async () => {
    try {
      const params = {
        limit: txnPageSize,
        offset: (txnPage - 1) * txnPageSize,
        sort_by: 'created_at',
        sort_order: 'desc',
      };
      if (txnFilters.organization_id) params.organization_id = txnFilters.organization_id;
      if (txnFilters.booking_type) params.booking_type = txnFilters.booking_type;
      if (txnFilters.payment_method) params.payment_method = txnFilters.payment_method;
      if (debouncedSearch) params.search = debouncedSearch;

      const res = await axios.get(`${apiBase}/admin/travel/transactions`, {
        params,
        headers: getAuthHeaders(),
      });
      if (res.data?.success) {
        setTransactions(res.data.transactions);
        setTxnTotalCount(res.data.total_count);
      }
    } catch (err) {
      console.error('Failed to load transactions:', err);
    }
  }, [apiBase, txnPage, txnFilters.organization_id, txnFilters.booking_type, txnFilters.payment_method, debouncedSearch]);

  // Load org profitability
  const loadProfitability = useCallback(async () => {
    try {
      const res = await axios.get(`${apiBase}/admin/travel/organizations`, {
        params: { period },
        headers: getAuthHeaders(),
      });
      if (res.data?.success) {
        setOrgProfitability(res.data.organizations);
        setPlatformTotals(res.data.platform_totals);
      }
    } catch (err) {
      console.error('Failed to load profitability:', err);
    }
  }, [apiBase, period]);

  // Initial load
  useEffect(() => {
    loadOrganizations();
  }, [loadOrganizations]);

  // Load data based on active tab
  useEffect(() => {
    setLoading(true);
    setError('');

    const load = async () => {
      try {
        if (activeTab === 'overview') {
          await Promise.all([loadBalance(), loadSummary()]);
        } else if (activeTab === 'transactions') {
          await loadTransactions();
        } else if (activeTab === 'profitability') {
          await loadProfitability();
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [activeTab, loadBalance, loadSummary, loadTransactions, loadProfitability]);

  // Reset page when txn filters change
  useEffect(() => {
    setTxnPage(1);
  }, [txnFilters.organization_id, txnFilters.booking_type, txnFilters.payment_method, debouncedSearch]);

  const formatCurrency = (amount, currency = 'USD') => {
    if (amount == null || isNaN(amount)) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  const totalTxnPages = Math.ceil(txnTotalCount / txnPageSize);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: TrendingUp },
    { id: 'transactions', label: 'Transactions', icon: DollarSign },
    { id: 'profitability', label: 'Profitability', icon: Building2 },
  ];

  const periods = [
    { value: 'week', label: 'Week' },
    { value: 'month', label: 'Month' },
    { value: 'quarter', label: 'Quarter' },
    { value: 'year', label: 'Year' },
  ];

  // ===== RENDER HELPERS =====

  const renderPeriodSelector = () => (
    <div className={`flex gap-1 rounded-lg p-1 bg-ui-sunk border border-ui-line`}>
      {periods.map((p) => (
        <button
          key={p.value}
          onClick={() => setPeriod(p.value)}
          className={`px-3 py-1 text-sm rounded-md transition-all duration-200 ${
            period === p.value
              ? 'bg-ui-primary text-ui-on-primary shadow-md shadow-ui-primary/20 font-medium'
              : 'text-ui-muted hover:text-ui-ink hover:bg-ui-hover'
          }`}
        >
          {p.label}
        </button>
      ))}
    </div>
  );

  const renderOrgFilter = (value, onChange) => (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={`border rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ui-blue-solid/50 transition-colors ${inputBg}`}
    >
      <option value="">All Organizations</option>
      {organizations.map((org) => (
        <option key={org.id} value={org.id}>
          {org.name}
        </option>
      ))}
    </select>
  );

  // ===== OVERVIEW TAB =====

  const renderOverview = () => (
    <div className="space-y-6">
      {/* Filters */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        {renderPeriodSelector()}
        <div className="flex items-center gap-3">
          {renderOrgFilter(overviewOrgId, setOverviewOrgId)}
          <button
            onClick={() => { loadBalance(); loadSummary(); }}
            className={`p-2 rounded-md text-ui-muted hover:text-ui-ink hover:bg-ui-hover`}
            title="Refresh"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Duffel Balance Card */}
      {balanceData && (
        <div className={`rounded-xl border p-5 ${
          balanceData.low_balance
            ? 'border-ui-amber-line bg-gradient-to-r from-ui-amber-bg to-ui-orange-bg/50'
            : 'border-ui-green-line bg-gradient-to-r from-ui-green-bg to-ui-teal-bg/50'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Wallet className={`h-8 w-8 ${balanceData.low_balance
                ? 'text-ui-amber-fg'
                : 'text-ui-green-fg'
              }`} />
              <div>
                <p className={`text-sm font-medium ${textSecondary}`}>Duffel Balance</p>
                <p className={`text-2xl font-bold ${textPrimary}`}>
                  {formatCurrency(
                    balanceData.data?.available_amount || balanceData.data?.amount,
                    balanceData.data?.currency
                  )}
                </p>
              </div>
            </div>
            {balanceData.low_balance && (
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-md ${
                'text-ui-amber-fg bg-ui-amber-solid/20'
              }`}>
                <AlertTriangle className="h-4 w-4" />
                <span className="text-sm font-medium">Low Balance — Threshold: {formatCurrency(balanceData.threshold)}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Summary Metrics */}
      {summaryData && (
        <>
          {/* Top-level metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className={`rounded-xl border p-4 ${cardBg}`}>
              <p className={`text-xs font-medium uppercase tracking-wider ${textSecondary}`}>Total Bookings</p>
              <p className={`text-2xl font-bold mt-1 ${textPrimary}`}>{summaryData.total_bookings}</p>
            </div>
            <div className={`rounded-xl border p-4 ${cardBg}`}>
              <p className={`text-xs font-medium uppercase tracking-wider ${textSecondary}`}>Customer Revenue</p>
              <p className={`text-2xl font-bold mt-1 ${textPrimary}`}>{formatCurrency(summaryData.total_revenue)}</p>
            </div>
            <div className={`rounded-xl border p-4 ${cardBg}`}>
              <p className={`text-xs font-medium uppercase tracking-wider ${textSecondary}`}>Total Margin</p>
              <p className={`text-2xl font-bold mt-1 ${greenText}`}>{formatCurrency(summaryData.total_margin)}</p>
            </div>
            <div className={`rounded-xl border p-4 ${cardBg}`}>
              <p className={`text-xs font-medium uppercase tracking-wider ${textSecondary}`}>Margin %</p>
              <p className={`text-2xl font-bold mt-1 text-ui-blue-fg`}>{summaryData.margin_percentage}%</p>
            </div>
          </div>

          {/* Flight vs Hotel Breakdown */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Flights */}
            <div className={`rounded-xl border p-5 ${cardBg}`}>
              <div className="flex items-center gap-2 mb-3">
                <Plane className={`h-5 w-5 text-ui-blue-fg`} />
                <h3 className={`font-semibold ${textPrimary}`}>Flights</h3>
                <span className={`text-sm ml-auto ${textSecondary}`}>{summaryData.flights?.count || 0} bookings</span>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className={textSecondary}>Customer Revenue</span><span className={`font-medium ${textTertiary}`}>{formatCurrency(summaryData.flights?.total_customer_revenue)}</span></div>
                <div className="flex justify-between"><span className={textSecondary}>Base Cost</span><span className={`font-medium ${textTertiary}`}>{formatCurrency(summaryData.flights?.total_base_cost)}</span></div>
                <div className="flex justify-between"><span className={textSecondary}>Booking Fees</span><span className={`font-medium ${greenText}`}>{formatCurrency(summaryData.flights?.total_booking_fees)}</span></div>
                <div className="flex justify-between"><span className={textSecondary}>Commission</span><span className={`font-medium ${greenText}`}>{formatCurrency(summaryData.flights?.total_commission)}</span></div>
                <div className={`flex justify-between border-t pt-2 border-ui-divider`}>
                  <span className={textSecondary}>Payment: Card</span>
                  <span className={`font-medium ${textTertiary}`}>{summaryData.flights?.card_payment_count || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className={textSecondary}>Payment: Balance</span>
                  <span className={`font-medium ${textTertiary}`}>{summaryData.flights?.balance_payment_count || 0}</span>
                </div>
              </div>
            </div>

            {/* Hotels */}
            <div className={`rounded-xl border p-5 ${cardBg}`}>
              <div className="flex items-center gap-2 mb-3">
                <Hotel className={`h-5 w-5 text-ui-violet-fg`} />
                <h3 className={`font-semibold ${textPrimary}`}>Hotels</h3>
                <span className={`text-sm ml-auto ${textSecondary}`}>{summaryData.hotels?.count || 0} bookings</span>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className={textSecondary}>Customer Revenue</span><span className={`font-medium ${textTertiary}`}>{formatCurrency(summaryData.hotels?.total_customer_revenue)}</span></div>
                <div className="flex justify-between"><span className={textSecondary}>Base Cost (Remit to Duffel)</span><span className={`font-medium ${textTertiary}`}>{formatCurrency(summaryData.hotels?.total_base_cost)}</span></div>
                <div className="flex justify-between"><span className={textSecondary}>Booking Fees</span><span className={`font-medium ${greenText}`}>{formatCurrency(summaryData.hotels?.total_booking_fees)}</span></div>
                <div className="flex justify-between"><span className={textSecondary}>Commission</span><span className={`font-medium ${greenText}`}>{formatCurrency(summaryData.hotels?.total_commission)}</span></div>
                <div className={`flex justify-between border-t pt-2 border-ui-divider`}>
                  <span className={textSecondary}>Prepaid Hotels</span>
                  <span className={`font-medium ${textTertiary}`}>{summaryData.hotels?.prepaid_count || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className={textSecondary}>Pay at Hotel</span>
                  <span className={`font-medium ${textTertiary}`}>{summaryData.hotels?.pay_at_hotel_count || 0}</span>
                </div>
                <div className={`flex justify-between border-t pt-2 border-ui-divider`}>
                  <span className={textSecondary}>Collected from Clients</span>
                  <span className={`font-medium ${textTertiary}`}>{formatCurrency(summaryData.hotels?.total_collected_from_clients)}</span>
                </div>
                <div className="flex justify-between">
                  <span className={textSecondary}>Remitted to Duffel</span>
                  <span className={`font-medium ${textTertiary}`}>{formatCurrency(summaryData.hotels?.total_remitted_to_duffel)}</span>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );

  // ===== TRANSACTIONS TAB =====

  const renderTransactions = () => (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        {renderOrgFilter(txnFilters.organization_id, (v) => setTxnFilters((f) => ({ ...f, organization_id: v })))}
        <select
          value={txnFilters.booking_type}
          onChange={(e) => setTxnFilters((f) => ({ ...f, booking_type: e.target.value }))}
          className={`border rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ui-blue-solid/50 transition-colors ${inputBg}`}
        >
          <option value="">All Types</option>
          <option value="flight">Flights</option>
          <option value="hotel">Hotels</option>
        </select>
        <select
          value={txnFilters.payment_method}
          onChange={(e) => setTxnFilters((f) => ({ ...f, payment_method: e.target.value }))}
          className={`border rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ui-blue-solid/50 transition-colors ${inputBg}`}
        >
          <option value="">All Payment Methods</option>
          <option value="balance">Balance</option>
          <option value="card">Card</option>
        </select>
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-ui-muted" />
          <input
            type="text"
            placeholder="Search booking ref, airline, hotel..."
            value={txnFilters.search}
            onChange={(e) => setTxnFilters((f) => ({ ...f, search: e.target.value }))}
            className={`w-full pl-9 pr-3 py-1.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-ui-blue-solid/50 transition-colors ${inputBg}`}
          />
        </div>
      </div>

      {/* Results count */}
      <div className={`text-sm ${textSecondary}`}>
        {txnTotalCount} transaction{txnTotalCount !== 1 ? 's' : ''} found
      </div>

      {/* Table */}
      <div className={`border rounded-xl overflow-hidden ${tableBorderColor} shadow-ui-card`}>
        <Table>
          <TableHeader>
            <TableRow className={`${tableHeaderBg} border-ui-line`}>
              <TableHead className={`text-xs font-semibold uppercase tracking-wider text-ui-muted`}>Date</TableHead>
              <TableHead className={`text-xs font-semibold uppercase tracking-wider text-ui-muted`}>Type</TableHead>
              <TableHead className={`text-xs font-semibold uppercase tracking-wider text-ui-muted`}>Organization</TableHead>
              <TableHead className={`text-xs font-semibold uppercase tracking-wider text-ui-muted`}>Description</TableHead>
              <TableHead className={`text-xs font-semibold uppercase tracking-wider text-ui-muted`}>Ref</TableHead>
              <TableHead className={`text-xs font-semibold uppercase tracking-wider text-right text-ui-muted`}>Base Cost</TableHead>
              <TableHead className={`text-xs font-semibold uppercase tracking-wider text-right text-ui-muted`}>Fee</TableHead>
              <TableHead className={`text-xs font-semibold uppercase tracking-wider text-right text-ui-muted`}>Commission</TableHead>
              <TableHead className={`text-xs font-semibold uppercase tracking-wider text-right text-ui-muted`}>Customer Price</TableHead>
              <TableHead className={`text-xs font-semibold uppercase tracking-wider text-ui-muted`}>Payment</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {transactions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} className={`text-center py-12 ${textSecondary}`}>
                  No transactions found
                </TableCell>
              </TableRow>
            ) : (
              transactions.map((txn, idx) => (
                <TableRow key={`${txn.type}-${txn.id}`} className={`text-sm transition-colors duration-150 ${hoverBg} ${idx % 2 === 0 ? 'bg-ui-surface/30' : 'bg-ui-surface/60'} border-ui-divider`}>
                  <TableCell className={`text-xs whitespace-nowrap ${textTertiary}`}>{formatDate(txn.created_at)}</TableCell>
                  <TableCell>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                      txn.type === 'flight'
                        ? 'bg-ui-blue-bg text-ui-blue-fg ring-1 ring-ui-blue-line'
                        : 'bg-ui-violet-bg text-ui-violet-fg ring-1 ring-ui-violet-line'
                    }`}>
                      {txn.type === 'flight' ? <Plane className="h-3 w-3" /> : <Hotel className="h-3 w-3" />}
                      {txn.type === 'flight' ? 'Flight' : 'Hotel'}
                    </span>
                    {txn.type === 'hotel' && txn.rate_payment_type && (
                      <span className={`ml-1 inline-flex px-1.5 py-0.5 rounded text-xs font-medium ${
                        txn.rate_payment_type?.toLowerCase() === 'pay_at_hotel' || txn.is_pay_at_hotel
                          ? 'bg-ui-amber-bg text-ui-amber-fg ring-1 ring-ui-amber-line'
                          : 'bg-ui-green-bg text-ui-green-fg ring-1 ring-ui-green-line'
                      }`}>
                        {txn.rate_payment_type?.toLowerCase() === 'pay_at_hotel' || txn.is_pay_at_hotel ? 'Pay@Hotel' : 'Prepaid'}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className={`text-xs max-w-[120px] truncate ${textTertiary}`} title={txn.organization_name}>
                    {txn.organization_name}
                  </TableCell>
                  <TableCell className={`text-xs max-w-[180px] truncate ${textPrimary}`} title={txn.description}>
                    {txn.description}
                  </TableCell>
                  <TableCell className={`text-xs font-mono max-w-[100px] truncate text-ui-muted`} title={txn.booking_reference}>
                    {txn.booking_reference || '—'}
                  </TableCell>
                  <TableCell className={`text-xs text-right tabular-nums ${textTertiary}`}>{formatCurrency(txn.base_price, txn.currency)}</TableCell>
                  <TableCell className={`text-xs text-right tabular-nums font-medium ${greenText}`}>{formatCurrency(txn.booking_fee, txn.currency)}</TableCell>
                  <TableCell className={`text-xs text-right tabular-nums ${greenText}`}>{formatCurrency(txn.commission, txn.currency)}</TableCell>
                  <TableCell className={`text-xs text-right tabular-nums font-semibold ${textPrimary}`}>{formatCurrency(txn.customer_price, txn.currency)}</TableCell>
                  <TableCell className="text-xs">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      txn.payment_method === 'balance'
                        ? 'bg-ui-indigo-bg text-ui-indigo-fg ring-1 ring-ui-indigo-line'
                        : 'bg-ui-hover text-ui-ink-2 ring-1 ring-ui-line'
                    }`}>
                      {txn.payment_method || '—'}
                    </span>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {totalTxnPages > 1 && (
        <div className="flex items-center justify-between">
          <span className={`text-sm ${textSecondary}`}>
            Page {txnPage} of {totalTxnPages}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setTxnPage((p) => Math.max(1, p - 1))}
              disabled={txnPage === 1}
              className={`flex items-center gap-1 px-4 py-2 text-sm border rounded-lg disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 ${
                'border-ui-control text-ui-ink-2 hover:bg-ui-hover hover:border-ui-dash'
              }`}
            >
              <ChevronLeft className="h-4 w-4" /> Previous
            </button>
            <button
              onClick={() => setTxnPage((p) => Math.min(totalTxnPages, p + 1))}
              disabled={txnPage === totalTxnPages}
              className={`flex items-center gap-1 px-4 py-2 text-sm border rounded-lg disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 ${
                'border-ui-control text-ui-ink-2 hover:bg-ui-hover hover:border-ui-dash'
              }`}
            >
              Next <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );

  // ===== PROFITABILITY TAB =====

  const renderProfitability = () => (
    <div className="space-y-4">
      {/* Period selector */}
      <div className="flex items-center justify-between">
        {renderPeriodSelector()}
        <button
          onClick={loadProfitability}
          className={`p-2 rounded-md text-ui-muted hover:text-ui-ink hover:bg-ui-hover`}
          title="Refresh"
        >
          <RefreshCw className="h-4 w-4" />
        </button>
      </div>

      {/* Table */}
      <div className={`border rounded-xl overflow-hidden ${tableBorderColor} shadow-ui-card`}>
        <Table>
          <TableHeader>
            <TableRow className={`${tableHeaderBg} border-ui-line`}>
              <TableHead className={`text-xs font-semibold uppercase tracking-wider text-ui-muted`}>Organization</TableHead>
              <TableHead className={`text-xs font-semibold uppercase tracking-wider text-right text-ui-muted`}>Flights</TableHead>
              <TableHead className={`text-xs font-semibold uppercase tracking-wider text-right text-ui-muted`}>Hotels</TableHead>
              <TableHead className={`text-xs font-semibold uppercase tracking-wider text-right text-ui-muted`}>Total</TableHead>
              <TableHead className={`text-xs font-semibold uppercase tracking-wider text-right text-ui-muted`}>Revenue</TableHead>
              <TableHead className={`text-xs font-semibold uppercase tracking-wider text-right text-ui-muted`}>Base Cost</TableHead>
              <TableHead className={`text-xs font-semibold uppercase tracking-wider text-right text-ui-muted`}>Fees</TableHead>
              <TableHead className={`text-xs font-semibold uppercase tracking-wider text-right text-ui-muted`}>Commission</TableHead>
              <TableHead className={`text-xs font-semibold uppercase tracking-wider text-right text-ui-muted`}>Margin</TableHead>
              <TableHead className={`text-xs font-semibold uppercase tracking-wider text-right text-ui-muted`}>Margin %</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {orgProfitability.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} className={`text-center py-12 ${textSecondary}`}>
                  No data for this period
                </TableCell>
              </TableRow>
            ) : (
              <>
                {orgProfitability.map((org, idx) => (
                  <TableRow key={org.organization_id} className={`text-sm transition-colors duration-150 ${hoverBg} ${idx % 2 === 0 ? 'bg-ui-surface/30' : 'bg-ui-surface/60'} border-ui-divider`}>
                    <TableCell className={`font-medium ${textPrimary}`}>{org.organization_name}</TableCell>
                    <TableCell className={`text-right tabular-nums ${textTertiary}`}>{org.flight_count}</TableCell>
                    <TableCell className={`text-right tabular-nums ${textTertiary}`}>{org.hotel_count}</TableCell>
                    <TableCell className={`text-right tabular-nums font-medium ${textPrimary}`}>{org.total_bookings}</TableCell>
                    <TableCell className={`text-right tabular-nums ${textTertiary}`}>{formatCurrency(org.total_customer_revenue)}</TableCell>
                    <TableCell className={`text-right tabular-nums ${textTertiary}`}>{formatCurrency(org.total_base_cost)}</TableCell>
                    <TableCell className={`text-right tabular-nums ${greenText}`}>{formatCurrency(org.total_booking_fees)}</TableCell>
                    <TableCell className={`text-right tabular-nums ${greenText}`}>{formatCurrency(org.total_commission)}</TableCell>
                    <TableCell className={`text-right tabular-nums font-bold ${greenBoldText}`}>{formatCurrency(org.total_margin)}</TableCell>
                    <TableCell className="text-right">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        org.margin_percentage >= 10
                          ? 'bg-ui-green-bg text-ui-green-fg ring-1 ring-ui-green-line'
                          : org.margin_percentage >= 5
                            ? 'bg-ui-amber-bg text-ui-amber-fg ring-1 ring-ui-amber-line'
                            : 'bg-ui-red-bg text-ui-red-fg ring-1 ring-ui-red-line'
                      }`}>
                        {org.margin_percentage}%
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
                {/* Platform Totals Row */}
                {platformTotals && (
                  <TableRow className={`font-semibold text-sm border-t-2 bg-gradient-to-r from-ui-hover to-ui-raised/50 border-ui-control`}>
                    <TableCell className={textPrimary}>Platform Total</TableCell>
                    <TableCell className={`text-right tabular-nums ${textTertiary}`}>{platformTotals.flight_count}</TableCell>
                    <TableCell className={`text-right tabular-nums ${textTertiary}`}>{platformTotals.hotel_count}</TableCell>
                    <TableCell className={`text-right tabular-nums ${textPrimary}`}>{platformTotals.total_bookings}</TableCell>
                    <TableCell className={`text-right tabular-nums ${textTertiary}`}>{formatCurrency(platformTotals.total_customer_revenue)}</TableCell>
                    <TableCell className={`text-right tabular-nums ${textTertiary}`}>{formatCurrency(platformTotals.total_base_cost)}</TableCell>
                    <TableCell className={`text-right tabular-nums ${greenText}`}>{formatCurrency(platformTotals.total_booking_fees)}</TableCell>
                    <TableCell className={`text-right tabular-nums ${greenText}`}>{formatCurrency(platformTotals.total_commission)}</TableCell>
                    <TableCell className={`text-right tabular-nums font-bold ${greenBoldText}`}>{formatCurrency(platformTotals.total_margin)}</TableCell>
                    <TableCell className="text-right">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        'bg-ui-blue-bg text-ui-blue-fg ring-1 ring-ui-blue-line'
                      }`}>
                        {platformTotals.margin_percentage}%
                      </span>
                    </TableCell>
                  </TableRow>
                )}
              </>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );

  // ===== MAIN RENDER =====

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className={`text-2xl font-bold bg-gradient-to-r from-ui-ink to-ui-ink-2 bg-clip-text text-transparent`}>Travel Transactions</h1>
        <p className={`text-sm mt-1 ${textSecondary}`}>Monitor Duffel transactions, booking fees, and profitability across organizations</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-ui-line">
        <div className="flex gap-6">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 pb-3 px-1 text-sm font-medium border-b-2 transition-all duration-200 ${
                  activeTab === tab.id
                    ? 'border-ui-link text-ui-link'
                    : 'border-transparent text-ui-muted hover:text-ui-ink'
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className={`rounded-xl p-4 flex items-center gap-2 text-sm ${
          'bg-ui-red-bg border border-ui-red-line text-ui-red-fg'
        }`}>
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Loading */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className={`h-8 w-8 animate-spin text-ui-link`} />
        </div>
      ) : (
        <>
          {activeTab === 'overview' && renderOverview()}
          {activeTab === 'transactions' && renderTransactions()}
          {activeTab === 'profitability' && renderProfitability()}
        </>
      )}
    </div>
  );
};

export default AdminTravelTransactions;
